import React, { forwardRef, useMemo } from 'react';
import { DynamicIcon } from 'lucide-react/dynamic';
import { Circle } from 'lucide-react';
import { materialToLucide } from './materialToLucide';
import './AppIcon.css';

const resolveLucideName = (materialName) => {
  if (!materialName || typeof materialName !== 'string') return null;
  const key = materialName.trim();
  return materialToLucide[key] || null;
};

/**
 * Substitui Material Symbols (fonte) por SVG Lucide — sem depender de Google Fonts.
 * @param {string} name — nome do ícone Material (ex.: 'home', 'calendar_month')
 * @param {string} [className]
 * @param {number|string} [size] — px (default 20)
 */
const AppIcon = forwardRef(function AppIcon(
  { name, className = '', size = 20, strokeWidth = 2, absoluteStrokeWidth, style, title, ...rest },
  ref
) {
  const lucideName = useMemo(() => resolveLucideName(name), [name]);

  const mergedClassName = ['app-icon', className].filter(Boolean).join(' ');
  const mergedStyle = useMemo(() => {
    const base = { ...style };
    const fs = style?.fontSize;
    if (fs != null && fs !== '') {
      base.width = fs;
      base.height = fs;
      base.minWidth = fs;
      base.minHeight = fs;
    } else {
      base.width = size;
      base.height = size;
      base.minWidth = size;
      base.minHeight = size;
    }
    return base;
  }, [size, style]);

  if (!lucideName) {
    if (import.meta.env?.DEV) {
      console.warn(`[AppIcon] Ícone Material desconhecido: "${name}"`);
    }
    return (
      <Circle
        ref={ref}
        className={mergedClassName}
        style={mergedStyle}
        strokeWidth={strokeWidth}
        absoluteStrokeWidth={absoluteStrokeWidth}
        aria-hidden={title ? undefined : true}
        title={title}
        {...rest}
      />
    );
  }

  return (
    <DynamicIcon
      ref={ref}
      name={lucideName}
      className={mergedClassName}
      style={mergedStyle}
      strokeWidth={strokeWidth}
      absoluteStrokeWidth={absoluteStrokeWidth}
      aria-hidden={title ? undefined : true}
      title={title}
      fallback={Circle}
      {...rest}
    />
  );
});

export default AppIcon;
